'use client';

interface GroupSettingToggleProps {
  /**
   * Stable accessible name for the switch, phrased as the ON-state action
   * (e.g. "Hide member list"). Combined with `aria-checked` a screen reader
   * announces "Hide member list, switch, on" — which states the setting AND
   * its effect. Do NOT pass a state-neutral noun ("Member list"): "switch, on"
   * would then be ambiguous about what "on" does.
   */
  label: string;
  /** True when the ON-state action named by `label` is active. */
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Visible label rendered left of the switch — the OFF-state meaning. */
  offLabel: string;
  /** Visible label rendered right of the switch — the ON-state meaning. */
  onLabel: string;
  /**
   * Per-instance test id root. Four of these render together on the group
   * settings screens, so every instance needs its own stable handle — tests
   * must never disambiguate sibling toggles by array index.
   */
  testIdPrefix: string;
  disabled?: boolean;
  /**
   * Id of an element explaining why the control is disabled. Wired to
   * `aria-describedby` so non-visual users get the reason, not just a dimmed
   * control.
   */
  disabledReasonId?: string;
}

/**
 * MWBI-890 — the single group-setting switch.
 *
 * TogetherWe asked for one consistent control across all four group settings
 * (Accepting Members, Member Approval, Member List, Group Events) that stays
 * green in BOTH the on and off positions. The green is therefore unconditional
 * styling: position, not colour, carries the state. Assistive tech reads state
 * from `aria-checked`, never from the fill, so the always-green look costs no
 * accessibility.
 */
export function GroupSettingToggle({
  label,
  checked,
  onChange,
  offLabel,
  onLabel,
  testIdPrefix,
  disabled = false,
  disabledReasonId,
}: GroupSettingToggleProps) {
  const handleToggle = () => {
    if (disabled) return;
    onChange(!checked);
  };

  return (
    <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-row`}>
      {/* Side labels are decorative: `label` + `aria-checked` already convey
          the setting and its state, so announcing both sides would duplicate
          it. Sighted users read position; screen readers read aria-checked. */}
      <span
        aria-hidden="true"
        data-testid={`${testIdPrefix}-off-label`}
        className={`text-sm ${checked ? 'text-foreground-muted' : 'text-foreground font-medium'}`}
      >
        {offLabel}
      </span>

      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        aria-describedby={disabled ? disabledReasonId : undefined}
        onClick={handleToggle}
        disabled={disabled}
        data-testid={`${testIdPrefix}-toggle`}
        className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-action-green transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>

      <span
        aria-hidden="true"
        data-testid={`${testIdPrefix}-on-label`}
        className={`text-sm ${checked ? 'text-foreground font-medium' : 'text-foreground-muted'}`}
      >
        {onLabel}
      </span>
    </div>
  );
}
